<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import { DangerBadge } from '@codinglabsau/ui'
import { InboxIcon } from '@heroicons/vue/24/outline'
import { Popover, PopoverButton, PopoverOverlay, PopoverPanel } from '@headlessui/vue'
import { useElementBounding } from '@vueuse/core'
import { ChatBubbleOvalLeftEllipsisIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/24/solid'
import { useEcho } from '@laravel/echo-vue'
import {
  accountMessagesTotal,
  accountThreads,
  groupedStoreThreads,
  lastParticipantType,
  messagesTotal,
  storeMessageCounters,
  storeMessagesTotal,
  stores,
  toggleThread,
} from '@/Stores/messagesDropdown.js'
import ThreadHeaderLink from '@/Components/Header/Messages/ThreadHeaderLink.vue'
import { tenantChannel } from '@/Composables/useBroadcasting'

defineProps({
  position: {
    type: String,
    default: 'top-0 -right-0.5',
  },
})

const page = usePage()

useEcho(
  tenantChannel('user.' + page.props.auth.user.id),
  ['.thread.updated', '.thread.last-read-updated'],
  (e) => toggleThread(e.thread),
)

page.props.auth.stores.forEach(store => {
  useEcho(
    tenantChannel('store.' + store.id),
    ['.thread.updated', '.thread.last-read-updated'],
    (e) => toggleThread(e.thread),
  )
})

const dropdown = ref(null)
const popoverPanel = ref(null)

const hiddenStores = ref([])

const activeTab = ref('account')

onMounted(() => {
  if (stores.value.length > 0) {
    activeTab.value = 'stores'
  }
})

watch(lastParticipantType, (newVal) => {
  if (newVal === 'store') {
    activeTab.value = 'stores'
  } else {
    activeTab.value = 'account'
  }
})

const { x: dropdownX, y: dropdownY, height: dropdownHeight, width: dropdownWidth } = useElementBounding(dropdown)
const { width: popoverWidth } = useElementBounding(popoverPanel)

const panelStyles = computed(() => {
  const left = (Math.floor(dropdownX.value) - ((popoverWidth.value / 2) - (dropdownWidth.value / 2)))
  return {
    top: (Math.floor(dropdownY.value) + dropdownHeight.value) + 'px',
    left: (left < 0 ? (document.documentElement.clientWidth - popoverWidth.value) / 2 : left) + 'px',
  }
})

const isAccountTabActive = computed(() => activeTab.value === 'account')
const isStoresTabActive = computed(() => activeTab.value === 'stores')

const toggleStore = (storeId) => {
  if (hiddenStores.value.includes(storeId)) {
    hiddenStores.value = hiddenStores.value.filter(id => id !== storeId)
  } else {
    hiddenStores.value.push(storeId)
  }
}

const hasStores = computed(() => page.props.auth.stores && page.props.auth.stores.length > 0)

const confirmMarkAllRead = () => {
  if (confirm(__('Are you sure you want to mark all threads as read?'))) {
    router.get(route('account.inbox.threads.mark-all-read'))
  }
}
</script>

<template>
  <Popover>
    <PopoverButton class="flex focus:ring-0 focus:outline-none select-none relative top-0 -right-0.5">
      <div ref="dropdown"
           class="flex flex-col items-center text-xs justify-center relative hover:underline">
        <component :is="ChatBubbleOvalLeftEllipsisIcon" class="md:w-8 md:h-8 h-6 w-6" />
        <span class="hidden md:block">
          {{ __('Messages') }}
        </span>
        <div v-if="messagesTotal && messagesTotal > 0">
          <span
            class="absolute bg-red-500 text-xs text-white rounded-md px-1"
            :class="position"
          >
            {{ messagesTotal }}
          </span>
        </div>
      </div>
    </PopoverButton>
    <PopoverOverlay class="fixed z-[20] inset-0" />
    <PopoverPanel class="absolute" v-slot="{ close }">
      <teleport to="body">
        <div
          ref="popoverPanel"
          class="absolute z-[21] border border-gray-200 rounded shadow-2xl w-[85vw] md:w-screen max-w-md max-h-[80vh] bg-white overflow-y-auto"
          :style="panelStyles"
        >
          <div v-if="hasStores" class="flex border-b">
            <button
              @click="activeTab = 'account'"
              :class="{'text-blue-500 border-b-2 border-blue-500': isAccountTabActive, 'text-gray-500': !isAccountTabActive}"
              class="py-3 text-sm font-medium w-1/2 text-center"
            >
              {{ __('My Messages') }}
              <DangerBadge
                v-if="accountMessagesTotal && accountMessagesTotal > 0"
                class="bg-red-500 !p-0 !w-5 !h-5 flex items-center justify-center !rounded-full text-white">
                {{ accountMessagesTotal }}
              </DangerBadge>
            </button>
            <button
              @click="activeTab = 'stores'"
              :class="{'text-blue-500 border-b-2 border-blue-500': isStoresTabActive, 'text-gray-500': !isStoresTabActive}"
              class="py-3 text-sm font-medium w-1/2 text-center"
            >
              {{ __('Stores') }}
              <DangerBadge
                v-if="storeMessagesTotal && storeMessagesTotal > 0"
                class="bg-red-500 !p-0 !w-5 !h-5 flex items-center justify-center !rounded-full text-white">
                {{ storeMessagesTotal }}
              </DangerBadge>
            </button>
          </div>

          <!-- Account Threads -->
          <div v-if="isAccountTabActive || !hasStores" class="w-full">
            <div class="flex items-center bg-white p-3 border-b">
              <div class="font-medium text-gray-700 flex-1">{{
                  __('Unread (:count)', { count: accountMessagesTotal })
                }}
              </div>
              <div class="flex items-center gap-4 text-gray-400 text-sm">
                <Link
                  class="text-indigo-500 flex items-center gap-1 hover:underline"
                  :href="route('account.inbox.threads.index')"
                  @click="close"
                >
                  <InboxIcon class="w-4 h-4" />
                  {{ __('All Messages') }}
                </Link>
              </div>
            </div>
            <div class="w-full divide-y" v-if="accountThreads.length > 0">
              <ThreadHeaderLink
                v-for="thread in accountThreads"
                :key="'account-' + thread.id"
                :thread="thread"
              />
            </div>
            <div v-else class="text-gray-500 p-4 text-sm text-center">{{ __('No new messages.') }}</div>
            <div
              v-if="accountMessagesTotal > 0"
              class="flex items-center p-3 justify-center border-t">
              <button
                class="text-indigo-500 text-sm hover:underline"
                type="button"
                @click.prevent="confirmMarkAllRead"
              >
                {{ __('Mark All Read') }}
              </button>
            </div>
          </div>

          <!-- Store Threads -->
          <div v-if="isStoresTabActive && hasStores">
            <div v-for="store in page.props.auth.stores" :key="store.id" class="flex flex-col gap-1">
              <div>
                <div class="flex items-center bg-white pr-3">
                  <button type="button" @click="toggleStore(store.id)" class="p-3">
                    <component :is="hiddenStores.includes(store.id) ? ChevronRightIcon : ChevronDownIcon"
                               class="h-5 w-5 text-gray-400" />
                  </button>
                  <div class="font-medium flex-1 text-gray-700">{{ store.name }}</div>
                  <div class="flex gap-2 text-gray-400 text-sm">
                    <Link
                      class="text-indigo-500"
                      :href="route('seller.stores.inbox.threads.index', [store.slug])"
                      @click="close"
                    >
                      {{
                        __('View All (:count)', { count: storeMessageCounters[store.id] ? storeMessageCounters[store.id] : 0 })
                      }}
                    </Link>
                  </div>
                </div>

                <div class="bg-gray-50 divide-y" v-if="!hiddenStores.includes(store.id)">
                  <template
                    v-if="groupedStoreThreads[store.id] && groupedStoreThreads[store.id].threads.length > 0"
                  >
                    <ThreadHeaderLink
                      v-for="thread in groupedStoreThreads[store.id].threads"
                      :key="'store-' + thread.id"
                      :thread="thread"
                    />
                  </template>
                  <div v-else class="text-gray-500 p-4 text-sm text-center">
                    {{ __(':name has no new messages.', { name: store.name }) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </teleport>
    </PopoverPanel>
  </Popover>
</template>
